import { styled, theme } from '@/src/stitches.config'
import { faArrowRight } from '@fortawesome/free-solid-svg-icons/faArrowRight'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Image from 'next/image'
import Link from 'next/link'
import { v4 as uuidv4 } from 'uuid'

const gradientStyles = {
    default: `-webkit-linear-gradient(74deg, ${theme.colors.primary} 21%, ${theme.colors.secondary} 88%)`,
    blue: `-webkit-linear-gradient(60deg, ${theme.colors.secondaryDark} 21%, ${theme.colors.secondary} 88%)`,
    gradientBorderDefault: `linear-gradient(white, white) padding-box, linear-gradient(to right, ${theme.colors.primary}, ${theme.colors.secondary}) border-box`,
    gradientBorderPG: `linear-gradient(white, white) padding-box, linear-gradient(to right, ${theme.colors.pgPrimary}, ${theme.colors.pgSecondary}) border-box`,
    gradientBorderBlue: `linear-gradient(white, white) padding-box, linear-gradient(to right, ${theme.colors.secondaryDark}, ${theme.colors.secondary}) border-box`,
}

const Wrapper = styled('div', {
    maxWidth: '100%',
    '@sm': {
        maxWidth: 450,
    },
    '@md': {
        maxWidth: 348,
    },
})

const CallToAction = styled('div', {
    display: 'flex',
    ai: 'center',
    maxWidth: '100%',
    gap: 15,
    p: 15,
    br: 15,
    mt: 15,
    variants: {
        gradient: {
            default: { background: gradientStyles.default },
            blue: { background: gradientStyles.blue },
            gradientBorderDefault: {
                background: gradientStyles.gradientBorderDefault,
                borderRadius: '8px',
                border: '2px solid transparent',
            },
            gradientBorderBlue: {
                background: gradientStyles.gradientBorderBlue,
                borderRadius: '8px',
                border: '2px solid transparent',
            },
            gradientBorderPG: {
                background: gradientStyles.gradientBorderPG,
                borderRadius: '8px',
                border: '2px solid transparent',
            },
        },
    },
    defaultVariants: {
        gradient: 'default',
    },
})

const StyledSvg = styled('svg', {
    height: 35,
    width: 35,
    minHeight: 35,
    minWidth: 35,
})

const CustomSVG = styled('div', {
    img: {
        height: 35,
        width: 35,
        minHeight: 35,
        minWidth: 35,
    },
})

const TextWrapper = styled('div', {
    display: 'flex',
    fd: 'column',
    gap: 10,
    '& p': {
        m: 0,
    },
    '& p:first-child': {
        fontWeight: 'bold',
    },
    variants: {
        textColor: {
            default: {
                '& p': {
                    color: '$white',
                },
            },
            black: {
                '& p': {
                    color: '$black',
                },
            },
        },
    },
})

const StyledDescription = styled('p', {
    margin: 'auto',
    textAlign: 'center',
    lineHeight: 1.25,
    pt: 10,
    maxWidth: '95%',
})

interface ICallToAction {
    url: string
    header?: string
    text?: string
    style?: React.CSSProperties
    gradientColor?:
        | 'default'
        | 'blue'
        | 'gradientBorderDefault'
        | 'gradientBorderBlue'
        | 'gradientBorderPG'
    customSVG?: string
}

const CallToActionButton = ({
    url = 'corporate/visa/predesign',
    header = 'PerfectGift.com Corporate',
    text = 'Buy Bulk Visa gift cards with your business logo',
    style = {},
    gradientColor = 'default',
    customSVG,
}: ICallToAction) => {
    const isGradientBorder = gradientColor.includes('gradient')
    const svgColorScheme = uuidv4()
    const usePGColorScheme = gradientColor === 'gradientBorderPG'

    return (
        <Wrapper>
            <Link
                href={url}
                target={'_blank'}
                style={{
                    textDecoration: 'none',
                }}
                data-testid={'cta-button'}
            >
                <CallToAction style={style} gradient={gradientColor}>
                    {!customSVG && (
                        <StyledSvg
                            xmlns="http://www.w3.org/2000/svg"
                            width={200}
                            height={60}
                            viewBox="0 0 72 58"
                            fill="none"
                        >
                            {isGradientBorder && (
                                <defs>
                                    <linearGradient
                                        id={svgColorScheme}
                                        x1="0%"
                                        y1="0%"
                                        x2="100%"
                                        y2="0%"
                                    >
                                        <stop
                                            offset="21%"
                                            style={{
                                                stopColor: String(usePGColorScheme ? theme.colors.pgPrimary : theme.colors.primary),
                                            }}
                                        />
                                        <stop
                                            offset="88%"
                                            style={{
                                                stopColor: String(usePGColorScheme ? theme.colors.pgSecondary : theme.colors.secondary),
                                            }}
                                        />
                                    </linearGradient>
                                </defs>
                            )}
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M26.6958 30.8142C26.6958 30.013 27.3745 29.3575 28.204 29.3575H34.2369C35.0665 29.3575 35.7452 30.013 35.7452 30.8142C35.7452 31.6153 35.0665 32.2708 34.2369 32.2708H28.204C27.3745 32.2708 26.6958 31.6153 26.6958 30.8142ZM38.7616 30.8142C38.7616 30.013 39.4403 29.3575 40.2698 29.3575H52.3356C53.1651 29.3575 53.8438 30.013 53.8438 30.8142C53.8438 31.6153 53.1651 32.2708 52.3356 32.2708H40.2698C39.4403 32.2708 38.7616 31.6153 38.7616 30.8142ZM17.6465 6.05119C17.6465 2.83292 20.3471 0.224609 23.6794 0.224609H65.9096C69.2371 0.224609 71.9425 2.83292 71.9425 6.05119V35.1841C71.9425 38.3978 69.2371 41.0107 65.9096 41.0107H63.3V43.7754C63.3 47.0891 60.6137 49.7754 57.3 49.7754H54.3V51.7754C54.3 55.0891 51.6137 57.7754 48.3 57.7754H6C2.68629 57.7754 0 55.0891 0 51.7754V22.9854C0 19.6717 2.68629 16.9854 5.99999 16.9854H9V14.9854C9 11.6717 11.6863 8.98539 15 8.98539H17.6465V6.05119ZM9 19.9854H5.99999C4.34314 19.9854 3 21.3285 3 22.9854V51.7754C3 53.4322 4.34314 54.7754 6 54.7754H48.3C49.9569 54.7754 51.3 53.4323 51.3 51.7754V49.7754H15C11.6863 49.7754 9 47.0891 9 43.7754V19.9854ZM23.6794 41.0107H60.3V43.7754C60.3 45.4323 58.9569 46.7754 57.3 46.7754H15C13.3431 46.7754 12 45.4322 12 43.7754V14.9854C12 13.3285 13.3431 11.9854 15 11.9854H17.6465V35.1841C17.6465 38.3978 20.3471 41.0107 23.6794 41.0107ZM20.6629 6.05119V8.96448H68.926V6.05119C68.926 4.44251 67.5781 3.1379 65.9096 3.1379H23.6794C22.0137 3.1379 20.6629 4.44251 20.6629 6.05119ZM20.6629 17.7044H68.926V11.8778H20.6629V17.7044ZM20.6629 20.6176V35.1841C20.6629 36.7955 22.0137 38.0974 23.6794 38.0974H65.9096C67.5781 38.0974 68.926 36.7955 68.926 35.1841V20.6176H20.6629Z"
                                fill={
                                    isGradientBorder
                                        ? `url(#${svgColorScheme})`
                                        : 'white'
                                }
                            />
                        </StyledSvg>
                    )}
                    {customSVG && (
                        <CustomSVG>
                            <Image
                                height={'58'}
                                width={'56'}
                                src={customSVG}
                                priority={true}
                                alt={'Arrow with loop'}
                            />
                        </CustomSVG>
                    )}
                    <TextWrapper
                        textColor={isGradientBorder ? 'black' : 'default'}
                    >
                        <p>{header}</p>
                    </TextWrapper>
                    <FontAwesomeIcon
                        icon={faArrowRight}
                        color={
                            isGradientBorder
                                ? String(theme.colors.secondary)
                                : 'white'
                        }
                        style={{ marginLeft: 'auto' }}
                    />
                </CallToAction>
                <StyledDescription>{text}</StyledDescription>
            </Link>
        </Wrapper>
    )
}

export default CallToActionButton
